import { Bot, X } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { articlesApi } from "../../api/articles.api";
import { usersApi } from "../../api/users.api";
import { ArticleDto } from "../../dto/article.dto";
import { useReadingTimer } from "../../hooks/useReadingTimer";
import { useUser } from "../../hooks/useUser";
import { useI18n } from "../../i18n/I18nProvider";
import { ArticleCard } from "../articles";
import { ProfileCard, QuestCard, SignUpCard } from "../cards";
import { ProgressCircle } from "../progress";
import TimerCircle from "./TimerCircle";
import io from "socket.io-client";
import { QuestDto } from "../../dto/quest.dto";

const socket = io(
  process.env.REACT_APP_BACKEND_URL || "https://api.read2earn.cointribune.com",
  {
    transports: ["websocket"],
  }
);

const Tracker: React.FC = () => {
  const { t } = useI18n();
  const { user, updateUser, clearUser } = useUser();

  const [article, setArticle] = useState<ArticleDto | null>(null);
  const [quest, setQuest] = useState<QuestDto | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentArticleId, setCurrentArticleId] = useState<
    string | undefined
  >();

  const articleUrl = useMemo(() => window.location.href, []);

  const handleTimerComplete = useCallback(() => {
    if (article) {
      setArticle({ ...article, isReaded: true });
    }
  }, [article]);

  const {
    timeElapsed,
    isPaused,
    isCompleted,
    start,
    reset,
    pause,
    resume,
    resetActivity,
  } = useReadingTimer({
    totalTime: article?.readingTime || 0,
    articleId: article?.id,
    onComplete: handleTimerComplete,
    autoValidate: true,
    questId: article?.quest?.id,
  });

  const fetchArticle = useCallback(async () => {
    try {
      const data = await articlesApi.getByUrl(articleUrl);
      setArticle(data);
      setQuest(data.quest);
    } catch {
      setArticle(null);
      setQuest(null);
    } finally {
      setIsLoading(false);
    }
  }, [articleUrl]);

  const fetchUser = useCallback(async () => {
    try {
      const userData = await usersApi.getInfo();
      updateUser(userData);
    } catch (err) {
      console.error("Error fetching user:", err);
      clearUser();
    } finally {
      setIsLoading(false);
    }
  }, [updateUser, clearUser]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        pause();
      } else {
        resetActivity();
        if (article && user && !article.isReaded && !isCompleted) {
          resume();
        }
      }
    };

    const handleWindowFocus = () => {
      resetActivity();
      if (article && user && !article.isReaded && !isCompleted) {
        resume();
      }
    };

    const handleWindowBlur = () => {
      pause();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleWindowFocus);
    window.addEventListener("blur", handleWindowBlur);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleWindowFocus);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, [article, user, isCompleted, pause, resume, resetActivity]);

  useEffect(() => {
    if (article?.id !== currentArticleId) {
      reset();
      setCurrentArticleId(article?.id);
    }
  }, [article?.id, currentArticleId, reset]);

  useEffect(() => {
    if (article && user && !article.isReaded && !isCompleted) {
      start();
    }
  }, [article, user, start, isCompleted]);

  useEffect(() => {
    fetchUser();
    fetchArticle();
  }, [fetchUser, fetchArticle]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const toggleModal = async () => {
    if (!isModalOpen) {
      await fetchArticle();
    }
    setIsModalOpen((val) => !val);
  };

  useEffect(() => {
    if (!article) return;

    socket.emit("join-article", article.id);

    const handleUpdate = () => {
      fetchArticle();
    };

    socket.on("article-updated", handleUpdate);

    return () => {
      socket.emit("leave-article", article.id);
      socket.off("article-updated", handleUpdate);
    };
  }, [article, fetchArticle]);

  return isLoading ? null : (
    <>
      <div className="fixed circle-background modal-trigger-position z-40">
        {article ? (
          <TimerCircle
            user={user}
            article={article}
            time={timeElapsed}
            totalTime={article.readingTime}
            isPaused={isPaused}
            isCompleted={isCompleted}
            onClick={toggleModal}
          />
        ) : (
          <ProgressCircle
            children={
              <Bot
                className="animate-bounce"
                style={{
                  color: "var(--color-primary)",
                  stroke: "var(--color-primary)",
                }}
              />
            }
            onClick={toggleModal}
          />
        )}
      </div>
      {isModalOpen && (
        <div className="rounded bg-surface p-4 flex flex-col gap-4 fixed modal-position modal-width modal-shadow">
          <button
            onClick={() => handleCloseModal()}
            className="absolute top-0 right-0 bg-primary text-white rounded-tr-2xl rounded-bl-2xl w-10 h-8 flex justify-center items-center cursor-pointer close-btn-shadow z-40"
          >
            <X size={20}></X>
          </button>
          {user ? (
            <>
              <ProfileCard />
              {article && (
                <>
                  <ArticleCard name={t("CurrentArticle")} article={article} />
                  {quest && <QuestCard quest={quest} />}
                </>
              )}
            </>
          ) : (
            <SignUpCard onClose={handleCloseModal} />
          )}
        </div>
      )}
    </>
  );
};

export default Tracker;
